<template>
  <BModal v-model="isVisible"
          size="lg"
          centered
          scrollable
          body-class="p-0"
          @keydown.enter="apply"
          content-class="rounded-xl overflow-hidden shadow-2xl border-0">
    <template #title>
      <h5 class="h5 mb-0 px-3">{{ placeholder }}</h5>
      <p class="text-muted mb-0 px-3 small">
        {{ $t('Webportal.HierarchicalSelection') }} <!-- von Fahrzeugkategorien, Marken und Unterkategorien -->
      </p>
    </template>

    <!-- Footer -->
    <template #footer>
      <div class="text-muted small me-auto">{{ visibleCount }} / {{ countAllLeaves(treeData) }} sichtbar</div>
      <div class="text-muted small">{{ $t('Webportal.CountOptionsSelected', { count: countCheckedLeaves(treeData) }) }}</div>
      <div class="d-flex gap-2">
        <BButton variant="outline-secondary" @click="close">{{ $t('Webportal.Cancel') }}</BButton>
        <BButton variant="primary" @click="apply">{{ $t('Webportal.Apply') }}</BButton>
      </div>
    </template>

      <!-- Body -->
    <div class="flex flex-col h-full">
      <div class="p-4 flex-grow-1 overflow-hidden d-flex flex-column">
        <!-- Search -->
        <div class="position-relative mb-3">
          <Search class="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" :size="16" />
          <input
            type="text"
            class="form-control ps-5"
            :placeholder="`${$t('Webportal.Search')}...`"
            v-model="searchQuery"
          >
        </div>

        <!-- Tree View -->
        <div class="rounded overflow-auto scroll-y scroll-on-hover" style="min-height: 50vh; max-height: 50vh;">
          <div v-for="group in filteredTreeData" :key="group.id" class="border-bottom last-border-bottom-0">
            <!-- Group Header -->
            <div class="p-2 d-flex align-items-center group-header">
              <button v-if="group.children && group.children.length > 0"
                      class="btn btn-sm btn-icon p-1 me-2 text-muted hover-bg-gray-200 rounded"
                      @click="toggleExpand(group)">
                <ChevronDown v-if="group.expanded" :size="16" />
                <ChevronRight v-else :size="16" />
              </button>

              <div class="d-flex align-items-center flex-grow-1 cursor-pointer" @click.stop="toggleGroup(group)">
                <div class="custom-checkbox me-2" :class="{ 'checked': group.checked, 'indeterminate': group.indeterminate }">
                  <Check v-if="group.checked" :size="12" class="text-white" />
                  <Minus v-else-if="group.indeterminate" :size="12" class="text-white" />
                </div>
                <span class="fw-medium user-select-none">{{ group.label }}</span>
              </div>
            </div>

            <!-- Group Children -->
            <div v-if="group.expanded" class="ps-4 border-top">
              <div  v-for="item in group.children"
                    :key="item.id"
                    class="p-2 d-flex align-items-center border-bottom last-border-bottom-0 hover-bg-light cursor-pointer"
                    @click="toggleItem(group, item)">
                <button class="btn btn-sm btn-icon p-1 me-2 invisible">
                  <ChevronRight :size="16" />
                </button>

                <div class="d-flex align-items-center flex-grow-1">
                  <div class="custom-checkbox me-2" :class="{ 'checked': item.checked }">
                    <Check v-if="item.checked" :size="12" class="text-white" />
                  </div>
                  <span class="user-select-none">{{ item.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </BModal>
</template>

<script>
import { Search, ChevronDown, ChevronRight, Check, Minus } from 'lucide-vue-next';
import { countCheckedLeaves, countAllLeaves } from '@/utils/treeHelpers';

export default {
  name: 'FilterModal',

  components: {
    Search,
    ChevronDown,
    ChevronRight,
    Check,
    Minus
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    filterName: {
      type: String,
      default: '',
      // required: true
    },
    placeholder: {
      type: String,
      default: 'Select Items'
    },
    initialData: {
      type: Array,
      default: () => []
    }
  },

  emits: ['update:modelValue', 'apply'],

  data() {
    return {
      searchQuery: '',
      treeData: [],
      visibleCount: 0
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    filteredTreeData() {
      if (!this.searchQuery) {
        this.visibleCount = this.countAllLeaves(this.treeData);
        return this.treeData;
      }

      const query = this.searchQuery.toLowerCase();

      let result = this.treeData.map(group => {
        const groupMatches = group.label.toLowerCase().includes(query);
        const matchingChildren = group.children ? group.children.filter(child =>
          child.label.toLowerCase().includes(query)
        ) : [];

        if (groupMatches || matchingChildren.length > 0) {
          // Wichtig: Originale Referenz verwenden, nur children und expanded überschreiben
          if (!groupMatches && matchingChildren.length > 0) {
            // Temporäres Objekt nur für die Anzeige
            const filteredGroup = Object.create(group);
            filteredGroup.children = matchingChildren;
            filteredGroup.expanded = true;
            return filteredGroup;
          }
          // Gruppe selbst matcht - alles anzeigen, nur expanded setzen
          group.expanded = true;
          return group;
        }
        return null;
      }).filter(group => group !== null);

      this.visibleCount = this.countAllLeaves(result);
      return result;
    }
  },

  methods: {
    countCheckedLeaves, countAllLeaves,

    close() {
      this.isVisible = false;
    },
    apply() {
      const selected = [];
      this.treeData.forEach(group => {
        if (group.children) {
          group.children.forEach(child => {
            if (child.checked) selected.push(child);
          });
        } else {
          if (group.checked) selected.push(group);
        }
      });
      // this.$emit('apply', selected, this.treeData);
      this.$emit('apply', this.filterName, JSON.parse(JSON.stringify(this.treeData)), selected);
      this.close();
    },
    toggleExpand(group) {
      group.expanded = !group.expanded;
    },
    toggleGroup(group) {
      const targetState = !group.checked;

      group.checked = targetState;
      group.indeterminate = false;

      if (group.children) {
        group.children.forEach(child => {
          child.checked = targetState;
        });
      }
    },
    toggleItem(group, item) {
      item.checked = !item.checked;
      this.updateGroupState(group);
    },
    updateGroupState(group) {
      if (!group.children) return;

      const allChecked = group.children.every(c => c.checked);
      const someChecked = group.children.some(c => c.checked);

      group.checked = allChecked;
      group.indeterminate = someChecked && !allChecked;
    }
  },

  watch: {
    initialData: {
      handler(newVal) {
        this.searchQuery = '';
        if (newVal) {
          // In a real app we would merge/map this. For now we keep the mock data or overwrite if provided
          let items = JSON.parse(JSON.stringify(newVal));
          if (items && !Array.isArray(items)) {
            items = [items];
          };
          items.forEach(group => {
            if (group.children) {
              if(group.expanded === undefined)
                group.expanded = true;

              group.children.forEach(child => {
                if (child.children && child.expanded === undefined)
                  child.expanded = true;
              });
            }
          });
          this.treeData = items;
        }
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid var(--bs-gray-400);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &.checked, &.indeterminate {
    background-color: var(--bs-primary); // Primary color
    border-color: var(--bs-primary);
  }
}

.hover-bg-light:hover {
  // background-color: var(--bs-gray-100);
}

.hover-bg-gray-200:hover {
  // background-color: var(--bs-gray-200);
}

.last-border-bottom-0:last-child {
  border-bottom: 0 !important;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}
</style>
